import { useState } from 'react';
import { Plus as AddIcon, Pencil as EditIcon, Trash2 as DeleteIcon } from 'lucide-react';
import { Button, Chip, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@coordio/ui';
import { EmptyState, FormSection } from '@coordio/ui/layout';
import { ParameterForm } from './ParameterForm';
import { useConfirmDialog } from '../common/ConfirmDialog';
import type { AgentParameterDto, CreateAgentParameterRequest, UpdateAgentParameterRequest } from '../../types/agent';

interface ParameterListProps {
  parameters: AgentParameterDto[];
  onAdd: (parameter: CreateAgentParameterRequest) => void;
  onUpdate: (id: string, parameter: UpdateAgentParameterRequest) => void;
  onDelete: (id: string) => void;
  readOnly?: boolean;
}

/**
 * Parameter list — FormSection + token-styled table for agent parameters.
 */
export const ParameterList = ({
  parameters,
  onAdd,
  onUpdate,
  onDelete,
  readOnly = false,
}: ParameterListProps) => {
  const confirm = useConfirmDialog();
  const [openForm, setOpenForm] = useState(false);
  const [editingParameter, setEditingParameter] = useState<AgentParameterDto | null>(null);

  const handleAdd = () => {
    setEditingParameter(null);
    setOpenForm(true);
  };

  const handleEdit = (parameter: AgentParameterDto) => {
    setEditingParameter(parameter);
    setOpenForm(true);
  };

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: 'Delete parameter',
      message: 'Are you sure you want to delete this parameter?',
      confirmText: 'Delete',
    });
    if (ok) {
      onDelete(id);
    }
  };

  const handleFormClose = () => {
    setOpenForm(false);
    setEditingParameter(null);
  };

  const handleFormSubmit = (data: CreateAgentParameterRequest | UpdateAgentParameterRequest) => {
    if (editingParameter) {
      onUpdate(editingParameter.id, data as UpdateAgentParameterRequest);
    } else {
      onAdd(data as CreateAgentParameterRequest);
    }
    handleFormClose();
  };

  return (
    <FormSection
      title="Parameters"
      description={parameters.length > 0 ? `${parameters.length} parameter${parameters.length !== 1 ? 's' : ''} defined` : undefined}
      data-testid="agent-section-parameters"
    >
      {!readOnly && (
        <div className="flex justify-end mb-3">
          <Button type="button" variant="outline" size="sm" onClick={handleAdd} data-testid="add-parameter-btn">
            <AddIcon size={16} className="mr-1" />
            Add Parameter
          </Button>
        </div>
      )}

      {parameters.length === 0 ? (
        <EmptyState
          title="No parameters"
          description={readOnly ? 'This agent has no parameters.' : 'Click "Add Parameter" to define key-value inputs the agent accepts at runtime.'}
        />
      ) : (
        <div className="border border-[var(--color-border-default)] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border-default)] bg-[var(--color-bg-subtle)]">
                <th className="px-3 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)]">Key</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)]">Label</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)]">Type</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)]">Default Value</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)]">Required</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)]">Secret</th>
                {!readOnly && <th className="px-3 py-2 text-right text-xs font-medium text-[var(--color-text-secondary)]">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {parameters.map((param) => (
                <tr key={param.id} className="border-b border-[var(--color-border-subtle)] last:border-b-0 hover:bg-[var(--color-bg-subtle)]">
                  <td className="px-3 py-2">
                    <span className="text-sm font-medium text-[var(--color-text-primary)]">{param.key}</span>
                  </td>
                  <td className="px-3 py-2 text-[var(--color-text-primary)]">{param.label}</td>
                  <td className="px-3 py-2">
                    <Chip label={param.type} variant="outlined" />
                  </td>
                  <td className="px-3 py-2">
                    <span className="text-sm text-[var(--color-text-secondary)]">
                      {param.isSecret ? (param.hasSecretConfigured ? '••••••••' : 'Not configured') : param.defaultValue || '—'}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {param.isRequired && <Chip label="Required" color="accent" />}
                  </td>
                  <td className="px-3 py-2">
                    {param.isSecret && <Chip label="Secret" color="amber" />}
                  </td>
                  {!readOnly && (
                    <td className="px-3 py-2 text-right">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button type="button" variant="ghost" size="icon" onClick={() => handleEdit(param)} aria-label="Edit parameter">
                              <EditIcon size={16} />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Edit</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="text-[var(--color-danger)]"
                              onClick={() => handleDelete(param.id)}
                              aria-label="Delete parameter"
                            >
                              <DeleteIcon size={16} />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Delete</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openForm && (
        <ParameterForm
          parameter={editingParameter}
          open={openForm}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
        />
      )}
    </FormSection>
  );
};
